import { html, LitElement, PropertyValues, TemplateResult, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { TimelineEvent, TimelineGroup, TimelineItem } from '../types';
import CollapsiblePanelController from '../../reactiveControllers/CollapsiblePanelController';
import { GzCollapsiblePanelWebComponent } from '../../gzCollapsiblePanel';
import { styles as componentStyles } from '../view/timelineStyles.scss';
import { styles as styleHelpersStyles } from '../../../assets/style-helpers.scss';
import BatchLoader from '../types/batchLoader';
import { isEmpty } from '@nirvana/shared/basic-types/object';

/**
 * @summary This component represents a collapsible list of timeline items, which can be grouped or ungrouped * @property name - The name of the card group
 * @property {TimelineData[]} data - The array of grouped or ungrouped timeline items
 * @tag gz-collapsible-timeline
 */
@customElement('gz-timeline')
export class GzTimelineWebComponent extends LitElement {
  @property({ type: Array })
  initialData: (TimelineGroup | TimelineItem)[] = [];

  @property({ type: Array })
  data: (TimelineGroup | TimelineItem)[] = [];

  @state()
  private openPanelId: string | null = null;

  @property({ type: Boolean, attribute: true, reflect: true })
  allowMultipleOpen = false;

  @state()
  private isLoadingUp = false;

  @state()
  private isLoadingDown = false;

  private collapsibleController = new CollapsiblePanelController<GzCollapsiblePanelWebComponent>(
    this as LitElement & HTMLElement,
    this.allowMultipleOpen
  );

  //the number of items to load at once
  private batchSize = 60;
  private batchLoader: BatchLoader;

  private lastClickedLabel: string | null = null;
  private scrollDisabled = false;

  static get styles() {
    return [unsafeCSS(`${componentStyles}`), unsafeCSS(`${styleHelpersStyles}`)];
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('scroll', this.onScroll);
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    if (_changedProperties.has('initialData')) {
      this.batchLoader = new BatchLoader(this.initialData as (TimelineGroup | TimelineItem)[], this.batchSize);
      this.data = this.batchLoader.getCurrentBatch();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('scroll', this.onScroll);
    super.disconnectedCallback();
  }

  private onScroll = () => {
    //bottom scroll event
    if (Math.ceil(this.scrollTop + this.clientHeight) >= Math.floor(this.scrollHeight)) {
      this.loadMoreData('down').then(r => setTimeout(() => (this.isLoadingDown = false), 300));
    }

    //top scroll event
    if (this.scrollTop === 0) {
      this.loadMoreData('up').then(r => setTimeout(() => (this.isLoadingUp = false), 300));
    }
  };

  private async loadMoreData(direction: 'up' | 'down') {
    const { top, bottom } = this.batchLoader.getEndOfData();

    if (direction === 'down') {
      if (bottom) {
        return;
      }
      this.scrollTop = this.scrollHeight - this.clientHeight - 50;
      this.isLoadingDown = true;
      this.scrollDisabled = false;
    }

    if (direction === 'up') {
      if (top) {
        return;
      }

      this.scrollTop = this.scrollDisabled ? 0 : 50;
      this.isLoadingUp = !this.scrollDisabled;
      this.scrollDisabled = false;
    }

    this.batchLoader.loadMoreData(direction);
    this.data = this.batchLoader.getCurrentBatch();
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('allowMultipleOpen')) {
      this.collapsibleController.setAllowMultipleOpen(this.allowMultipleOpen);
    }

    if (_changedProperties.has('initialData')) {
      this.batchLoader = new BatchLoader(this.initialData as (TimelineGroup | TimelineItem)[], this.batchSize);
      this.data = this.batchLoader.getCurrentBatch();
    }
  }

  public resetScroll() {
    this.scrollDisabled = true;
    this.scrollTop = 0;
    this.isLoadingUp = false;
    this.isLoadingDown = false;
  }

  private renderCategoryItems(label: string, counter: number, id: string): TemplateResult {
    return html`
      <div class="header-container pt-1 pb-1">
        <div class="header" @click="${this.headerClick}" groupId="${id}">
          <span class="name">${label}</span>
          <span class="count">(${counter})</span>
        </div>
      </div>
    `;
  }

  private headerClick(e: CustomEvent): void {
    const target = e.currentTarget as HTMLElement;
    const targetId = target.getAttribute('groupId');

    if (targetId) {
      this.removeLastHeaderToggle(targetId);
    }

    if (target.hasAttribute('highlight')) {
      target.removeAttribute('highlight');
    } else {
      target.setAttribute('highlight', 'true');
    }

    this.clearOpenedPanel();

    const group = this.data.filter(({ id }) => {
      return id === target.getAttribute('groupId');
    });

    this.dispatchEvent(new CustomEvent(TimelineEvent.SLOT_HEADER_CLICK, { detail: { group: group, target: target } }));
  }

  private removeLastHeaderToggle(targetId: string): void {
    const oldClickedLabel = this.lastClickedLabel;
    this.lastClickedLabel = this.lastClickedLabel === targetId ? null : targetId;

    //query the last label clicked
    if (this.lastClickedLabel || this.openPanelId) {
      const label = this.shadowRoot?.querySelector(`[groupId="${oldClickedLabel}"]`) as HTMLElement;
      if (label) {
        label.removeAttribute('highlight');
      }
    }
  }

  public clearHighlight() {
    const label = this.shadowRoot?.querySelector(`[highlight="true"]`) as HTMLElement;
    if (label) {
      label.removeAttribute('highlight');
      this.lastClickedLabel = null;
    }
  }

  public resetTimeline() {
    this.clearHighlight();
    this.clearOpenedPanel();
  }

  public clearOpenedPanel() {
    this.openPanelId = null;
    this.collapsibleController.clearPanels();
  }

  private onToggleItem(e: CustomEvent<{ id: string }>): void {
    const itemId = e.detail.id;
    const wasOpen = this.openPanelId !== null;
    this.openPanelId = this.openPanelId === itemId ? null : itemId;
    if (this.openPanelId || wasOpen) {
      this.dispatchEvent(
        new CustomEvent(TimelineEvent.TOGGLE_ITEM_IN_GROUP, {
          detail: { id: itemId, isOpen: this.openPanelId !== null },
        })
      );
    }

    this.removeLastHeaderToggle(itemId);
  }

  private renderItems(item: any): TemplateResult {
    const isOpen = this.openPanelId === item.id;
    return html`<gz-collapsible-timeline-item
      .id=${item.id}
      .label=${item.label}
      .details=${item.details}
      ?isOpen=${isOpen}
      .suffixRenderer=${item.suffixRenderer}
      .contentRenderer=${item.contentRenderer}
      ?isEmphasized=${item.isEmphasized}
      .groupedEntriesCount=${item.groupedEntriesCount}
      @toggleItem="${this.onToggleItem}"
    ></gz-collapsible-timeline-item> `;
  }

  private renderTimeline(item: any, isCategory: boolean): TemplateResult {
    if (isCategory) {
      const id = item.id;
      const label = item.label;
      const counter = item.items.length;

      return html`${this.renderCategoryItems(label, counter, id)}`;
    }

    return html`${this.renderItems(item)}`;
  }

  render(): TemplateResult {
    return html`
      ${this.isLoadingUp ? html`<gz-progress-spinner indeterminate spinnerWidth="medium"></gz-progress-spinner>` : ''}
      ${this.data.map((item: TimelineItem | TimelineGroup) => {
        if ('isCategory' in item && item.isCategory) {
          return html`${this.renderTimeline(item, true)}`;
        }

        return html`${this.renderTimeline(item, false)}`;
      })}
      ${this.isLoadingDown ? html`<gz-progress-spinner indeterminate spinnerWidth="medium"></gz-progress-spinner>` : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-timeline': GzTimelineWebComponent;
  }
}
